// /src/pages/private/members/personalhub/dashboard/cards/DashboardStructureStrip.jsx
// input-security: verified — fields: dashboard-structure-request-comment

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StructureRecapStrip from '../../../shared/StructureRecapStrip';
import OrgStructureSelector from '../../../shared/OrgStructureSelector';
import {
  resolveDashboardStructureCards,
  structureCardsToSelection,
} from '../structureCardsUtils';
import {
  saveDashboardOrgStructure,
  ackDashboardOrgStructureResolveMessage,
} from '../dashboardGameloopTagsApi';
import { IconPen, IconCheck } from '../dashboardGameloopIcons';
import ActiveAssignmentNotice from '../../../shared/ActiveAssignmentNotice';
import {
  MAX_REQUEST_COMMENT_LENGTH,
  sanitizeRequestCommentDraft,
} from '../../../../../../utils/sanitizeUserTextInput';

const DASHBOARD_STRUCTURE_SAVE_ROUTE = '/api/user/private-dashboard/org-structure-save';
const DASHBOARD_STRUCTURE_RESOLVE_ACK_ROUTE =
  '/api/user/private-dashboard/org-structure-resolve-ack';
const REQUEST_COMMENT_MAX_LEN = MAX_REQUEST_COMMENT_LENGTH;

export default function DashboardStructureStrip({
  handleId,
  player,
  loading,
  canEdit = false,
  pendingChangeRequest = null,
  lastResolvedChangeRequest = null,
  activeAssignment = null,
  membershipAssignmentId = null,
  membershipAssignmentName = null,
  onPendingChangeRequest,
  onResolvedMessageAck,
  onStructureSaved,
  onEditingChange,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveInfo, setSaveInfo] = useState('');
  const [draftCanConfirm, setDraftCanConfirm] = useState(false);
  const [viewingPending, setViewingPending] = useState(false);
  const [requestComment, setRequestComment] = useState('');
  const [resolvedMessageAckId, setResolvedMessageAckId] = useState(null);
  const [dismissingResolveNote, setDismissingResolveNote] = useState(false);
  const [resolveDismissError, setResolveDismissError] = useState('');
  const selectorRef = useRef(null);

  const hid = Number(handleId);
  const hidOk = Number.isInteger(hid) && hid >= 1;
  const hasPending = Boolean(pendingChangeRequest?.id);

  const currentCards = useMemo(() => resolveDashboardStructureCards(player), [player]);
  const pendingCards = useMemo(() => {
    const fromApi = pendingChangeRequest?.requestedStructureCards;
    return Array.isArray(fromApi) ? fromApi : [];
  }, [pendingChangeRequest?.requestedStructureCards]);

  const displayCards = viewingPending && hasPending ? pendingCards : currentCards;
  const initialSelection = useMemo(
    () => structureCardsToSelection(currentCards, membershipAssignmentId),
    [currentCards, membershipAssignmentId],
  );

  const lockMembershipEdit = hasPending;

  useEffect(() => {
    if (!hasPending) {
      setViewingPending(false);
    }
  }, [hasPending]);

  const togglePendingView = useCallback(() => {
    setViewingPending((prev) => !prev);
  }, []);

  const openEdit = useCallback(() => {
    setSaveError('');
    setSaveInfo('');
    setViewingPending(false);
    setDraftCanConfirm(false);
    setRequestComment(
      sanitizeRequestCommentDraft(
        pendingChangeRequest?.requestComment
          ? String(pendingChangeRequest.requestComment)
          : '',
      ),
    );
    setEditing(true);
  }, [pendingChangeRequest?.requestComment]);

  const showResolvedNote =
    lastResolvedChangeRequest?.resolveComment &&
    lastResolvedChangeRequest.id !== resolvedMessageAckId &&
    !hasPending;

  const ackResolvedNote = useCallback(async () => {
    const changeRequestId = lastResolvedChangeRequest?.id;
    if (!changeRequestId || !hidOk || dismissingResolveNote) return;

    setDismissingResolveNote(true);
    setResolveDismissError('');
    try {
      const data = await ackDashboardOrgStructureResolveMessage(hid, changeRequestId);
      if (!data?.ok) {
        throw new Error(data?.error || 'Could not dismiss message.');
      }
      setResolvedMessageAckId(changeRequestId);
      if (typeof onResolvedMessageAck === 'function') {
        onResolvedMessageAck(changeRequestId);
      }
    } catch (e) {
      setResolveDismissError(e?.message || 'Could not dismiss message.');
    } finally {
      setDismissingResolveNote(false);
    }
  }, [
    lastResolvedChangeRequest?.id,
    hidOk,
    dismissingResolveNote,
    hid,
    onResolvedMessageAck,
  ]);

  const handleDraftChange = useCallback((state) => {
    setDraftCanConfirm(Boolean(state?.canConfirm));
  }, []);

  const handleStructureConfirm = useCallback(
    async (choice) => {
      if (!hidOk) {
        throw new Error('No active handle.');
      }
      const effectiveMembershipId = lockMembershipEdit
        ? membershipAssignmentId
        : choice.membershipAssignmentId;

      const data = await saveDashboardOrgStructure(hid, {
        divisionId: choice.divisionId,
        branchId: choice.branchId,
        sectionId: choice.sectionId,
        membershipAssignmentId: effectiveMembershipId,
        requestComment: sanitizeRequestCommentDraft(requestComment).trim() || null,
      });
      if (!data?.ok) {
        throw new Error(data?.error || 'Could not submit assignment change.');
      }
      setSaveInfo(
        data?.message ||
          (data?.membershipOnly
            ? 'Your membership title has been updated.'
            : 'Your assignment change has been submitted for approval.'),
      );
      if (data?.membershipOnly) {
        if (typeof onStructureSaved === 'function') {
          onStructureSaved(data);
        }
      } else if (typeof onPendingChangeRequest === 'function') {
        onPendingChangeRequest(data.pendingChangeRequest || null);
      }
      setViewingPending(false);
      setEditing(false);
    },
    [
      hid,
      hidOk,
      lockMembershipEdit,
      membershipAssignmentId,
      onPendingChangeRequest,
      onStructureSaved,
      requestComment,
    ],
  );

  const handleSave = useCallback(async () => {
    if (saving || !draftCanConfirm || !selectorRef.current) return;
    setSaving(true);
    setSaveError('');
    try {
      await selectorRef.current.confirm();
    } catch (e) {
      setSaveError(e?.message || 'Could not save assignment.');
    } finally {
      setSaving(false);
    }
  }, [saving, draftCanConfirm]);

  const showPen = canEdit && !editing && !loading;
  const showValidate = editing;

  useEffect(() => {
    if (typeof onEditingChange === 'function') {
      onEditingChange(editing);
    }
  }, [editing, onEditingChange]);

  const structureHead = (
    <div className="member-dashboard__structure-head">
      <h2 className="member-dashboard__structure-title">Assignment</h2>
      <div className="member-dashboard__gameloop-actions">
        {hasPending && !editing ? (
          <button
            type="button"
            className={
              'member-dashboard__structure-pending-toggle' +
              (viewingPending ? ' member-dashboard__structure-pending-toggle--active' : '')
            }
            onClick={togglePendingView}
            aria-pressed={viewingPending}
            aria-label={
              viewingPending
                ? 'View current assignment'
                : 'View pending change request'
            }
            data-resource="private.navbar.personalhub.structure"
            data-action="read"
            data-description="Toggle pending assignment preview"
          >
            {viewingPending ? 'View current assignment' : 'View pending change request'}
          </button>
        ) : null}

        {showPen ? (
          <button
            type="button"
            className="member-dashboard__gameloop-icon-btn"
            onClick={openEdit}
            aria-label="Edit assignment"
            data-resource="private.navbar.personalhub.structure"
            data-action="execute"
            data-route={DASHBOARD_STRUCTURE_SAVE_ROUTE}
            data-description="Open assignment editor"
          >
            <IconPen className="member-dashboard__gameloop-icon" />
          </button>
        ) : null}

        {showValidate ? (
          <button
            type="button"
            className={
              'member-dashboard__gameloop-validate' +
              (draftCanConfirm && !saving
                ? ''
                : ' member-dashboard__gameloop-validate--disabled')
            }
            disabled={saving || !draftCanConfirm}
            onClick={handleSave}
            aria-label={saving ? 'Saving assignment' : 'Validate assignment'}
            data-resource="private.navbar.personalhub.structure"
            data-action="write"
            data-route={DASHBOARD_STRUCTURE_SAVE_ROUTE}
            data-description="Save org structure assignment"
          >
            {saving ? (
              <span className="member-dashboard__gameloop-validate-dots">…</span>
            ) : (
              <IconCheck className="member-dashboard__gameloop-icon member-dashboard__gameloop-icon--check" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );

  if (loading && !player && !editing) {
    return (
      <div
        className="member-dashboard__structure-block"
        data-resource="private.navbar.personalhub.structure"
        data-action="read"
        data-description="Personal hub — assignment"
      >
        {structureHead}
        <div className="member-dashboard__structure-block-body">
          <div
            className="member-dashboard__structure-strip member-dashboard__structure-strip--loading"
            aria-busy="true"
            aria-label="Loading org structure"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="member-dashboard__structure-block"
      data-resource="private.navbar.personalhub.structure"
      data-action="read"
      data-description="Personal hub — assignment"
    >
      {structureHead}

      <div className="member-dashboard__structure-block-body">
      {(editing || hasPending) && activeAssignment?.hasActiveAssignment ? (
        <ActiveAssignmentNotice activeAssignment={activeAssignment} variant="dashboard" />
      ) : null}

      {showResolvedNote ? (
        <div
          className={
            'member-dashboard__structure-resolve-note' +
            (lastResolvedChangeRequest.status === 'approved'
              ? ' member-dashboard__structure-resolve-note--approved'
              : ' member-dashboard__structure-resolve-note--dismissed')
          }
          role="note"
        >
          <p className="member-dashboard__structure-resolve-note-label">
            {lastResolvedChangeRequest.status === 'approved'
              ? 'Your last assignment change request was approved'
              : 'Your last assignment change request was declined'}
          </p>
          <p className="member-dashboard__structure-resolve-note-body">
            {lastResolvedChangeRequest.resolveComment}
          </p>
          <button
            type="button"
            className="member-dashboard__structure-resolve-note-dismiss"
            onClick={ackResolvedNote}
            disabled={dismissingResolveNote}
            data-resource="private.navbar.personalhub.structure"
            data-action="write"
            data-route={DASHBOARD_STRUCTURE_RESOLVE_ACK_ROUTE}
            data-description="Dismiss resolved request message"
          >
            {dismissingResolveNote ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      ) : null}

      {resolveDismissError ? (
        <p
          className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--error"
          role="alert"
        >
          {resolveDismissError}
        </p>
      ) : null}

      {hasPending && !editing ? (
        <p className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--info" role="status">
          A division, branch, or section change is awaiting approval. Your membership title cannot
          be changed until this request is resolved.
        </p>
      ) : null}

      {viewingPending && hasPending ? (
        <p className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--info" role="status">
          Showing your pending assignment change (awaiting approval).
        </p>
      ) : null}

      {viewingPending && hasPending && pendingChangeRequest?.requestComment ? (
        <p className="member-dashboard__structure-request-note" role="note">
          <span className="member-dashboard__structure-request-note-label">
            Your comment
          </span>
          {pendingChangeRequest.requestComment}
        </p>
      ) : null}

      {saveError ? (
        <p
          className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--error"
          role="alert"
        >
          {saveError}
        </p>
      ) : null}

      {saveInfo ? (
        <p className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--info" role="status">
          {saveInfo}
        </p>
      ) : null}

      {editing ? (
        <>
          <OrgStructureSelector
            ref={selectorRef}
            key={`dashboard-structure-edit-${initialSelection.divisionId}-${initialSelection.branchId}-${initialSelection.sectionId}-${initialSelection.membershipAssignmentId}`}
            tagsHelpIdPrefix="dashboard-structure"
            wrapClassName="member-dashboard__structure-editor"
            cardItemClassName="member-dashboard__structure-editor__card-item"
            initialSelection={initialSelection}
            masterTags={[]}
            lockMembershipEdit={lockMembershipEdit}
            lockSectionsWithoutValidTitles
            showInternalConfirm={false}
            showFooterDock={false}
            onDraftChange={handleDraftChange}
            onConfirm={handleStructureConfirm}
            validateHelpText={
              lockMembershipEdit
                ? 'Submit your requested division, branch, and section change for approval.'
                : 'Change structure and/or membership title, then validate. Structure changes require approval; membership updates apply immediately when structure is unchanged.'
            }
            confirmLabel="Save assignment"
          />
          <div className="member-dashboard__structure-comment">
            <label
              className="member-dashboard__structure-comment-label"
              htmlFor="dashboard-structure-request-comment"
            >
              Comment for reviewers (optional)
            </label>
            <p className="member-dashboard__structure-comment-hint">
              Explain why you are requesting this change. Officers will see this when
              reviewing your request.
            </p>
            <textarea
              id="dashboard-structure-request-comment"
              className="member-dashboard__structure-comment-textarea"
              rows={3}
              maxLength={REQUEST_COMMENT_MAX_LEN}
              value={requestComment}
              onChange={(e) => setRequestComment(sanitizeRequestCommentDraft(e.target.value))}
              disabled={saving}
              placeholder="Optional context for your request…"
              data-resource="private.navbar.personalhub.structure"
              data-action="write"
              data-route={DASHBOARD_STRUCTURE_SAVE_ROUTE}
            />
          </div>
        </>
      ) : displayCards.length > 0 ? (
        <>
          <StructureRecapStrip
            variant="dashboard"
            cards={displayCards}
            dataResource="private.navbar.personalhub.structure"
            dataDescription={
              viewingPending
                ? 'Personal hub — pending assignment preview'
                : 'Personal hub — structure recap'
            }
            rootClassName={
              'member-dashboard__structure-strip' +
              (viewingPending ? ' member-dashboard__structure-strip--pending-preview' : '')
            }
          />
          {!viewingPending && membershipAssignmentName ? (
            <p className="member-dashboard__structure-membership-recap" role="status">
              <span className="member-dashboard__structure-membership-recap-label">
                Membership title
              </span>
              {membershipAssignmentName}
            </p>
          ) : null}
        </>
      ) : viewingPending && hasPending ? (
        <p className="member-dashboard__gameloop-empty">
          Pending assignment details are not available.
        </p>
      ) : canEdit ? (
        <p className="member-dashboard__gameloop-empty">
          No assignment is set yet. Use edit to choose your division, branch, and section.
        </p>
      ) : null}
      </div>
    </div>
  );
}
